<template>
  <div>
    <b-modal
      v-model="isModalVisible"
      title="Manual Price Feed Update"
      size="lg"
      centered
      scrollable
      header-bg-variant="light"
      @hidden="resetModal"
    >
      <div class="update-feed-modal">
        <div class="mb-4">
          <MetaMaskConnector
            @connected="handleMetaMaskConnected"
            @disconnected="handleMetaMaskDisconnected"
            @network-changed="handleNetworkChanged"
            @error="handleMetaMaskError"
          />
          <b-alert
            v-if="wrongNetworkSelection"
            show
            variant="warning"
            class="mb-2"
          >
            <i class="fa fa-exclamation-triangle mr-2"></i>
            You are connected to <strong>{{ currentNetworkName }}</strong> but
            this feed is on
            <strong>{{ feedData.network?.name || "Unknown Network" }}</strong
            >. Please switch your network in MetaMask.
          </b-alert>
        </div>

        <div class="feed-info mb-4" v-if="feedData">
          <strong>By confirming this action, you will:</strong>
          <div class="details">
            <ul>
              <li>Submit the latest market price data to the blockchain</li>
              <li>
                Sign a transaction from your connected wallet via RedStone
              </li>
              <li>Pay gas fees (calculated by MetaMask)</li>
            </ul>
          </div>

          <p class="note">
            This manual update ensures price accuracy and reliability in the
            oracle network. Your contribution maintains data integrity for all
            dependent DeFi protocols.
          </p>
          <div class="feed-info-grid">
            <div class="feed-info-item">
              <span class="feed-info-label">Network:</span>
              <span class="feed-info-value">
                <img
                  class="feeds__token-image small"
                  v-if="feedData"
                  :src="feedData?.network.image"
                  :alt="feedData?.network.name"
                />
                {{ feedData.network?.name }}</span
              >
            </div>
            <div class="feed-info-item">
              <span class="feed-info-label">Contract Address:</span>
              <span class="feed-info-value">
                <a
                  class="feeds__contract-address"
                  :title="`Open address in ${feedData.explorer.name} explorer`"
                  target="_blank"
                  :href="`${feedData.explorer.explorerUrl}/address/${feedData.contract_address}`"
                >
                  {{ truncateString(feedData.contract_address) }}
                </a>
                <CopyToClipboard
                  copy-text="Copy address"
                  :value="feedData.contract_address"
                />
              </span>
            </div>
            <div class="feed-info-item">
              <span class="feed-info-label">Feed ID:</span>
              <span class="feed-info-value">
                <img
                  :src="feedData.token_image?.logoURI"
                  class="feeds__token-image"
                  :alt="feedData.feed"
                />
                {{ feedData.token }}</span
              >
            </div>
            <div class="feed-info-item">
              <span class="feed-info-label">Current Value:</span>
              <strong
                style="font-weight: 500"
                v-if="feedData.apiValues?.value"
                v-html="
                  parseToCurrency(
                    feedData.apiValues?.value,
                    denominationMap[feedData.token]?.denomination,
                    feedData.feed
                  )
                "
              ></strong>

              <Loader
                v-else-if="feedData.loaders?.feedDataValue"
                class="feeds__loader"
              />
              <span v-else-if="feedData.answer">
                <strong
                  style="font-weight: 500"
                  v-html="
                    parseToCurrency(
                      feedData.answer,
                      denominationMap[feedData.token]?.denomination,
                      feedData.feed,
                      true
                    )
                  "
                >
                </strong>
              </span>
              <span v-else class="feeds__no-data">no-data</span>
            </div>
          </div>
        </div>

        <div class="transaction-status" v-if="transactionStatus">
          <b-alert :show="true" variant="light" class="mb-4">
            <div class="d-flex align-items-center">
              <div v-if="transactionStatus === 'pending'" class="mr-3">
                <b-spinner small></b-spinner>
              </div>
              <div v-else-if="transactionStatus === 'success'" class="mr-3">
                <i
                  class="fa fa-check-circle text-success status-icon"
                  style=""
                ></i>
              </div>
              <div v-else-if="transactionStatus === 'error'" class="mr-3">
                <i class="fa fa-times-circle text-danger status-icon"></i>
              </div>
              <div>
                <strong>{{ transactionStatusText }}</strong>
                <div v-if="transactionHash" class="mt-2">
                  <small>
                    Transaction Hash:
                    <a
                      :href="getTransactionExplorerUrl(transactionHash)"
                      target="_blank"
                      class="text-break"
                    >
                      {{ truncateString(transactionHash, 20) }}
                    </a>
                    <CopyToClipboard
                      copy-text="Copy tx hash"
                      :value="transactionHash"
                    />
                  </small>
                </div>
                <div v-if="transactionError" class="mt-2">
                  <small>{{ transactionError }}</small>
                </div>
              </div>
            </div>
          </b-alert>
        </div>

        <div
          class="update-feed-form"
          v-if="
            (!transactionStatus || transactionStatus === 'error') && feedData
          "
        >
          <b-form
            @submit.prevent="updateFeedWithRedstone"
            :title="updateButtonTooltip"
            v-b-tooltip.hover
          >
            <b-button
              type="submit"
              pill
              variant="outline-success"
              :disabled="!canUpdate"
              block
              class="update-button"
            >
              <i class="fa fa-sync-alt mr-2"></i>
              {{ updateButtonText }}
            </b-button>
          </b-form>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import { ethers } from "ethers";
  import { WrapperBuilder } from "@redstone-finance/evm-connector";
  import MetaMaskConnector from "./MetaMaskConnector.vue";
  import CopyToClipboard from "@/pages/Redstone/Feeds/components/CopyToClipboard.vue";
  import { parseToCurrency } from "@/pages/Redstone/Feeds/utils/FeedsTableDataLayer.js";
  import { mapGetters } from "vuex";
  import {
    getSignersForDataServiceId,
    getOracleRegistryStateSync,
  } from "@redstone-finance/sdk";

  const CONTRACT_ABI = [
    {
      name: "updateDataFeedsValuesPartial",
      type: "function",
      stateMutability: "nonpayable",
      inputs: [
        {
          name: "dataFeedsIds",
          type: "bytes32[]",
          internalType: "bytes32[]",
        },
      ],
      outputs: [],
    },
  ];
  const REDSTONE_SERVICE_PROVIDER = "redstone-primary-prod";

  export default {
    name: "UpdateFeedModal",
    components: {
      MetaMaskConnector,
      CopyToClipboard,
    },
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      feedData: {
        type: Object,
        default: null,
      },
    },
    data() {
      return {
        isModalVisible: false,
        provider: null,
        signer: null,
        account: null,
        chainId: null,
        currentNetworkName: null,
        isConnected: false,
        transactionStatus: null, // null, 'pending', 'success', 'error'
        transactionHash: null,
        transactionError: null,
      };
    },
    computed: {
      updateButtonText() {
        return "Push on-chain update";
      },
      updateButtonTooltip() {
        if (!this.isConnected) {
          return "Connect MetaMask wallet";
        }
        return this.wrongNetworkSelection
          ? "Wrong MetaMask network"
          : "Update price by signing with your wallet address";
      },
      wrongNetworkSelection() {
        return this.isConnected && !this.isCorrectNetwork && this.feedData;
      },
      ...mapGetters("manifests", ["denominationMap"]),
      isCorrectNetwork() {
        return (
          this.isConnected &&
          this.feedData &&
          this.chainId === this.feedData.network?.id
        );
      },
      canUpdate() {
        return this.isConnected && this.isCorrectNetwork && this.feedData;
      },
      transactionStatusText() {
        switch (this.transactionStatus) {
          case "pending":
            return "Transaction in progress...";
          case "success":
            return "Transaction successful! The feed will be updated shortly.";
          case "error":
            return "Transaction failed";
          default:
            return "";
        }
      },
    },
    watch: {
      value(newVal) {
        this.isModalVisible = newVal;
      },
      isModalVisible(newVal) {
        this.$emit("input", newVal);
      },
    },
    methods: {
      parseToCurrency,
      truncateString(str, length = 8) {
        if (!str) return "";
        if (str.length <= length * 2 + 3) return str;
        return `${str.slice(0, length)}...${str.slice(-length)}`;
      },

      handleMetaMaskConnected(data) {
        this.provider = data.provider;
        this.signer = data.signer;
        this.account = data.account;
        this.chainId = data.chainId;
        this.currentNetworkName = data.networkName;
        this.isConnected = true;
      },

      handleMetaMaskDisconnected() {
        this.provider = null;
        this.signer = null;
        this.account = null;
        this.chainId = null;
        this.currentNetworkName = null;
        this.isConnected = false;
      },

      handleNetworkChanged(data) {
        this.chainId = data.chainId;
        this.currentNetworkName = data.networkName;
        this.provider = data.provider;
        this.signer = data.signer;
      },

      handleMetaMaskError(error) {
        console.error("MetaMask error:", error);
        this.$bvToast.toast(error, {
          title: "MetaMask Error",
          variant: "danger",
          solid: true,
        });
      },

      formatToBytes32(str) {
        return ethers.utils.formatBytes32String(str);
      },

      async updateFeedWithRedstone() {
        if (!this.canUpdate) return;

        try {
          this.transactionStatus = "pending";
          this.transactionHash = null;
          this.transactionError = null;

          // Validate token existence
          if (!this.feedData || !this.feedData.token) {
            throw new Error("Feed token is not defined");
          }

          // Convert feed token to bytes32
          const feedIdBytes32 = this.formatToBytes32(this.feedData.token);
          console.log("Feed ID bytes32:", feedIdBytes32);

          // Prepare feed IDs array
          const feedIds = [feedIdBytes32];
          console.log("Feed IDs array:", feedIds);

          // Create a contract instance with the provider
          console.log("Contract provider: ", this.provider);
          const contract = new ethers.Contract(
            this.feedData.contract_address,
            CONTRACT_ABI,
            this.provider
          );
          try {
            const state = getOracleRegistryStateSync();
            const wrappedContract = WrapperBuilder.wrap(
              contract
            ).usingDataService({
              dataServiceId: REDSTONE_SERVICE_PROVIDER,
              uniqueSignersCount: 3,
              dataPackagesIds: [this.feedData.token],
              authorizedSigners: getSignersForDataServiceId(
                state,
                REDSTONE_SERVICE_PROVIDER
              ),
            });

            // Get the populated transaction
            const populatedTx =
              await wrappedContract.populateTransaction.updateDataFeedsValuesPartial(
                feedIds
              );

            // Send transaction
            const txHash = await window.ethereum.request({
              method: "eth_sendTransaction",
              params: [
                {
                  to: this.feedData.contract_address, // contract_address is  AdapterAddress in manifest
                  from: this.account,
                  data: populatedTx.data,
                },
              ],
            });

            this.transactionHash = txHash;
            console.log("Transaction sent:", txHash);
            const receipt = await this.provider.waitForTransaction(txHash);

            // Success
            if (receipt.status === 1) {
              this.transactionStatus = "success";
              this.$emit("update-success", {
                feedId: this.feedData.token,
                transactionHash: this.transactionHash,
              });
            } else {
              throw new Error("Transaction failed");
            }
          } catch (error) {
            console.error("Transaction error:", error);
            this.transactionStatus = "error";
            if (error.code === 4001) {
              // User rejected transaction
              this.transactionError = "Transaction was rejected by user";
            } else if (error.data && error.data.message) {
              this.transactionError = error.data.message;
            } else {
              this.transactionError = error.message || "Unknown error";
            }

            // Emit error event
            this.$emit("update-error", {
              feedId: this.feedData.token,
              error: error,
            });
          }
        } catch (error) {
          console.error("Transaction error:", error);
        }
      },

      getTransactionExplorerUrl(hash) {
        if (!hash || !this.feedData?.explorer?.explorerUrl) return "#";

        const baseUrl = this.feedData.explorer.explorerUrl;
        const txSlug = this.feedData.explorer.txSlug || "tx";

        return `${baseUrl}/${txSlug}/${hash}`;
      },

      closeModal() {
        this.isModalVisible = false;
      },

      resetModal() {
        this.transactionStatus = null;
        this.transactionHash = null;
        this.transactionError = null;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .update-feed-modal {
    max-width: 100%;
  }

  .feed-info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    background-color: #f8f9fa;
    border-radius: 6px;
  }

  .details {
    margin-left: 20px;
    ul {
      list-style: disc;
      margin-top: 10px;
    }
  }

  .feed-info-item {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    background-color: #fff;
    border-radius: 15px;
    padding: 10px;
    img {
      margin: 0;
    }
  }

  .feed-info-label {
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 4px;
  }

  .feed-info-value {
    font-weight: 500;
  }

  ::v-deep .modal-footer {
    display: none;
  }
  ::v-deep .modal-title {
    font-size: 14px;
  }

  .update-button:disabled {
    cursor: not-allowed;
    background-color: #fff;
    color: #21ae8c;
    &:hover {
      background-color: #fff;
    }
  }
  .status-icon {
    font-size: 35px;
  }
</style>
